(function (root, factory) {
  const api = factory(root);

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.NitigoScenarioShowcase = api;
  }

  if (root && root.document) {
    api.mountAll();

    if (!root.__nitigoScenarioShowcaseEventsBound) {
      root.__nitigoScenarioShowcaseEventsBound = true;
      root.document.addEventListener('shopify:section:load', (event) => {
        api.mountAll(event.target);
      });
    }
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
  const MOBILE_ANCHORS = {
    indoor: {
      refrigerator: { x: 4, y: 24 },
      toaster: { x: 26, y: 25 },
      coffee_machine: { x: 43, y: 25 },
      kettle: { x: 36, y: 25 },
      desk_lamp: { x: 28, y: 54 },
      floor_lamp: { x: 76, y: 25 },
      tower_fan: { x: 82, y: 38 },
      cpap: { x: 66, y: 39 },
      wifi_router: { x: 45, y: 71 },
      desktop_pc: { x: 8, y: 65 }
    },
    outdoor: {
      camping_lamp: { x: 23, y: 41 },
      smartphone_charging: { x: 17, y: 58 },
      projector: { x: 35, y: 55 },
      fan: { x: 57, y: 54 },
      laptop: { x: 47, y: 55 },
      camera_charger: { x: 61, y: 61 },
      coffee_machine: { x: 66, y: 56 },
      kettle: { x: 66, y: 56 },
      toaster: { x: 76, y: 59 },
      induction_hob: { x: 68, y: 64 },
      wifi_router: { x: 82, y: 55 },
      bluetooth_speaker: { x: 83, y: 60 },
      cooler: { x: 89, y: 66 }
    }
  };
  const MOBILE_LABELS = {
    indoor: {
      refrigerator: { x: 18, y: 19 },
      toaster: { x: 31, y: 18 },
      coffee_machine: { x: 50, y: 19 },
      kettle: { x: 31, y: 32 },
      desk_lamp: { x: 20, y: 48 },
      floor_lamp: { x: 85, y: 19 },
      tower_fan: { x: 90, y: 34 },
      cpap: { x: 61, y: 32 },
      wifi_router: { x: 55, y: 65 },
      desktop_pc: { x: 16, y: 61 }
    },
    outdoor: {
      camping_lamp: { x: 14, y: 36 },
      smartphone_charging: { x: 9, y: 53 },
      projector: { x: 32, y: 48 },
      fan: { x: 58, y: 46 },
      laptop: { x: 46, y: 61 },
      camera_charger: { x: 58, y: 67 },
      coffee_machine: { x: 67, y: 48 },
      kettle: { x: 67, y: 48 },
      toaster: { x: 79, y: 52 },
      induction_hob: { x: 69, y: 71 },
      wifi_router: { x: 88, y: 48 },
      bluetooth_speaker: { x: 89, y: 66 },
      cooler: { x: 92, y: 73 }
    }
  };

  function clampPercent(value) {
    return Math.min(100, Math.max(0, Number(value) || 0));
  }

  function formatDeviceValue(device) {
    const power = `${device.powerW}W`;
    return device.usageMinutes ? `${power} × ${device.usageMinutes} Min.` : power;
  }

  function normalizeImageSource(source) {
    if (typeof source === 'string') {
      return { src: source, srcset: '', sizes: '' };
    }

    if (!source || typeof source !== 'object') {
      return { src: '', srcset: '', sizes: '' };
    }

    return {
      src: source.src || '',
      srcset: source.srcset || '',
      sizes: source.sizes || ''
    };
  }

  function resolveImageSources(productImages, scenarioId) {
    const source = productImages ? productImages[scenarioId] : null;

    if (typeof source === 'string') {
      const legacySource = normalizeImageSource(source);
      return { desktop: legacySource, mobile: { ...legacySource } };
    }

    if (!source || typeof source !== 'object') {
      return {
        desktop: normalizeImageSource(null),
        mobile: normalizeImageSource(null)
      };
    }

    const desktop = normalizeImageSource(source.desktop || source.mobile);
    const mobile = normalizeImageSource(source.mobile || source.desktop);
    return { desktop, mobile };
  }

  function getMobileAnchor(scenarioId, deviceId, fallbackAnchor) {
    const scenarioAnchors = MOBILE_ANCHORS[scenarioId] || {};
    return scenarioAnchors[deviceId] || fallbackAnchor;
  }

  function getMobileCallout(scenarioId, deviceId, fallbackAnchor) {
    const target = getMobileAnchor(scenarioId, deviceId, fallbackAnchor);
    const scenarioLabels = MOBILE_LABELS[scenarioId] || {};
    const fallbackLabel = {
      x: clampPercent(target.x + (target.x > 50 ? -9 : 9)),
      y: clampPercent(target.y - 7)
    };

    return {
      label: scenarioLabels[deviceId] || fallbackLabel,
      target
    };
  }

  function buildMobileLeaderPoints(label, target) {
    const deltaX = target.x - label.x;
    const deltaY = target.y - label.y;
    const round = (value) => Math.round(value * 100) / 100;
    const elbow = Math.abs(deltaX) >= Math.abs(deltaY)
      ? { x: round(label.x + (deltaX * 0.55)), y: label.y }
      : { x: label.x, y: round(label.y + (deltaY * 0.55)) };

    return `${label.x},${label.y} ${elbow.x},${elbow.y} ${target.x},${target.y}`;
  }

  function buildSceneViewModel(dataset, productId, scenarioId) {
    const product = dataset.products.find((item) => item.id === productId) || dataset.products[0];
    const scenario = product.scenes[scenarioId] || product.scenes.indoor;

    return {
      product,
      scenarioId,
      summary: scenario.summary,
      devices: scenario.devices.map((device, index) => ({
        ...device,
        number: index + 1,
        value: formatDeviceValue(device),
        anchor: {
          x: clampPercent(device.anchor.x),
          y: clampPercent(device.anchor.y)
        },
        labelPosition: {
          x: clampPercent(device.labelPosition.x),
          y: clampPercent(device.labelPosition.y)
        }
      }))
    };
  }

  function parseJson(scope, selector) {
    const node = scope.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('NITIGO scenario data could not be parsed.', error);
      return null;
    }
  }

  function createElement(tagName, className, text) {
    const element = root.document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function clearNode(node) {
    if (node) node.replaceChildren();
  }

  function renderDesktopAnnotations(section, view) {
    const container = section.querySelector('[data-desktop-annotations]');
    const lines = section.querySelector('[data-annotation-lines]');
    if (!container || !lines) return;

    clearNode(container);
    clearNode(lines);

    view.devices.forEach((device) => {
      const label = createElement('div', 'scenario-showcase__annotation');
      label.dataset.deviceId = device.id;
      label.style.left = `${device.labelPosition.x}%`;
      label.style.top = `${device.labelPosition.y}%`;

      const name = createElement('span', 'scenario-showcase__annotation-name', device.label);
      const value = createElement('strong', 'scenario-showcase__annotation-value', device.value);
      label.append(name, value);
      container.append(label);

      const line = root.document.createElementNS(SVG_NAMESPACE, 'line');
      line.setAttribute('x1', device.labelPosition.x);
      line.setAttribute('y1', device.labelPosition.y);
      line.setAttribute('x2', device.anchor.x);
      line.setAttribute('y2', device.anchor.y);
      line.setAttribute('vector-effect', 'non-scaling-stroke');
      lines.append(line);

      const anchor = root.document.createElementNS(SVG_NAMESPACE, 'circle');
      anchor.setAttribute('cx', device.anchor.x);
      anchor.setAttribute('cy', device.anchor.y);
      anchor.setAttribute('r', '0.55');
      anchor.setAttribute('vector-effect', 'non-scaling-stroke');
      lines.append(anchor);
    });
  }

  function renderMobileHotspots(section, view) {
    const container = section.querySelector('[data-mobile-hotspots]');
    const lines = section.querySelector('[data-mobile-annotation-lines]');
    if (!container || !lines) return;

    clearNode(container);
    clearNode(lines);

    view.devices.forEach((device) => {
      const callout = getMobileCallout(view.scenarioId, device.id, device.anchor);
      const leader = root.document.createElementNS(SVG_NAMESPACE, 'polyline');
      leader.setAttribute('points', buildMobileLeaderPoints(callout.label, callout.target));
      leader.setAttribute('vector-effect', 'non-scaling-stroke');
      lines.append(leader);

      const hotspot = createElement('span', 'scenario-showcase__hotspot', String(device.number));
      hotspot.style.left = `${callout.label.x}%`;
      hotspot.style.top = `${callout.label.y}%`;
      hotspot.setAttribute('role', 'img');
      hotspot.setAttribute('aria-label', `${device.number}. ${device.label}, ${device.value}`);
      container.append(hotspot);
    });
  }

  function renderLegend(section, view) {
    const container = section.querySelector('[data-mobile-legend]');
    if (!container) return;

    clearNode(container);

    const headingRow = createElement('div', 'scenario-showcase__legend-heading');
    const heading = createElement('h3', '', `Was kann die ${view.product.name} versorgen?`);
    const scenario = createElement('span', '', view.scenarioId === 'indoor' ? 'Indoor' : 'Outdoor');
    headingRow.append(heading, scenario);

    const grid = createElement('div', 'scenario-showcase__legend-grid');
    view.devices.forEach((device) => {
      const item = createElement('div', 'scenario-showcase__legend-item');
      const number = createElement('span', 'scenario-showcase__legend-number', String(device.number));
      const copy = createElement('span', 'scenario-showcase__legend-copy');
      const name = createElement('span', 'scenario-showcase__legend-name', device.label);
      const value = createElement('strong', 'scenario-showcase__legend-value', device.value);
      copy.append(name, value);
      item.append(number, copy);
      grid.append(item);
    });

    const disclaimerSource = section.querySelector('[data-disclaimer-source]');
    const disclaimer = createElement(
      'p',
      'scenario-showcase__legend-disclaimer',
      disclaimerSource ? disclaimerSource.textContent.trim() : ''
    );

    container.append(headingRow, grid);
    if (disclaimer.textContent) container.append(disclaimer);
  }

  function renderSummary(section, view) {
    const container = section.querySelector('[data-scene-summary]');
    if (!container) return;

    clearNode(container);

    const copy = createElement('span', 'scenario-showcase__summary-copy');
    const prefix = createElement('span', '', view.summary.prefix);
    const duration = createElement('strong', '', view.summary.durationDisplay);
    copy.append(prefix, duration);

    const specs = createElement('span', 'scenario-showcase__summary-specs');
    const capacity = createElement('span', '', `${view.product.capacityWh}Wh`);
    const output = createElement('span', '', `${view.product.outputW}W`);
    specs.append(capacity, output);

    container.append(copy, specs);
  }

  function setActiveStates(section, state) {
    section.querySelectorAll('[data-product-id]').forEach((button) => {
      const active = button.dataset.productId === state.productId;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
    });

    section.querySelectorAll('[data-scenario-id]').forEach((button) => {
      const active = button.dataset.scenarioId === state.scenarioId;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
    });
  }

  function getViewportImageSource(sources) {
    const usesMobileImage = root.matchMedia
      ? root.matchMedia('(max-width: 768px)').matches
      : Number(root.innerWidth) <= 768;

    return usesMobileImage ? sources.mobile : sources.desktop;
  }

  function syncImageAspectRatio(section) {
    const image = section.querySelector('[data-scene-image]');
    const visual = section.querySelector('[data-scene-visual]');
    if (!image || !visual) return;

    const applyNaturalRatio = () => {
      if (!image.naturalWidth || !image.naturalHeight) return;
      visual.style.aspectRatio = `${image.naturalWidth} / ${image.naturalHeight}`;
    };

    if (image.complete) applyNaturalRatio();
    image.addEventListener('load', applyNaturalRatio);
  }

  function preloadImageSource(source, preloadCache) {
    const normalizedSource = normalizeImageSource(source);
    if (!normalizedSource.src) return Promise.resolve(null);

    const cacheKey = [normalizedSource.src, normalizedSource.srcset, normalizedSource.sizes].join('|');
    if (preloadCache.has(cacheKey)) return preloadCache.get(cacheKey);

    const preloadPromise = new Promise((resolve, reject) => {
      const loader = new Image();
      loader.decoding = 'async';

      if (normalizedSource.srcset) loader.srcset = normalizedSource.srcset;
      if (normalizedSource.sizes) loader.sizes = normalizedSource.sizes;

      loader.onload = () => {
        const decodePromise = typeof loader.decode === 'function'
          ? loader.decode().catch(() => undefined)
          : Promise.resolve();

        decodePromise.then(() => resolve(loader));
      };
      loader.onerror = () => reject(new Error(`Scenario image could not be loaded: ${normalizedSource.src}`));
      loader.src = normalizedSource.src;
    }).catch((error) => {
      preloadCache.delete(cacheKey);
      throw error;
    });

    preloadCache.set(cacheKey, preloadPromise);
    return preloadPromise;
  }

  function applyImageSource(element, source, fallbackToSrc) {
    if (!element) return;

    const responsiveSource = source.srcset || (fallbackToSrc ? source.src : '');
    if (responsiveSource) element.setAttribute('srcset', responsiveSource);
    else element.removeAttribute('srcset');

    if (source.sizes) element.setAttribute('sizes', source.sizes);
    else element.removeAttribute('sizes');
  }

  function applyImageSources(image, mobileSource, sources) {
    applyImageSource(mobileSource, sources.mobile, true);
    applyImageSource(image, sources.desktop, false);

    if (image.getAttribute('src') !== sources.desktop.src) {
      image.setAttribute('src', sources.desktop.src);
    }
  }

  async function updateImage(section, imageData, view, preloadCache, isCurrentRender) {
    const image = section.querySelector('[data-scene-image]');
    const mobileSource = section.querySelector('[data-scene-mobile-source]');
    const placeholder = section.querySelector('[data-scene-placeholder]');
    const visual = section.querySelector('[data-scene-visual]');
    const productImages = imageData && imageData.products ? imageData.products[view.product.id] : null;
    const sources = resolveImageSources(productImages, view.scenarioId);
    if (!image || !placeholder || !visual) return false;

    if (!sources.desktop.src && !sources.mobile.src) {
      image.hidden = true;
      placeholder.hidden = false;
      return false;
    }

    const viewportSource = getViewportImageSource(sources);
    let loadedImage;

    try {
      loadedImage = await preloadImageSource(viewportSource, preloadCache);
    } catch (error) {
      console.error(error);
      if (!isCurrentRender()) return false;
      if (!image.complete || !image.naturalWidth) {
        image.hidden = true;
        placeholder.hidden = false;
      }
      return false;
    }

    if (!isCurrentRender()) return false;
    applyImageSources(image, mobileSource, sources);
    image.alt = `${view.product.name} – ${view.scenarioId === 'indoor' ? 'Indoor' : 'Outdoor'}`;
    image.hidden = false;
    placeholder.hidden = true;

    if (loadedImage && loadedImage.naturalWidth && loadedImage.naturalHeight) {
      visual.style.aspectRatio = `${loadedImage.naturalWidth} / ${loadedImage.naturalHeight}`;
    }

    return true;
  }

  function renderView(section, state, view) {
    setActiveStates(section, state);
    renderDesktopAnnotations(section, view);
    renderMobileHotspots(section, view);
    renderLegend(section, view);
    renderSummary(section, view);
  }

  async function update(section, state, dataset, imageData, preloadCache) {
    if (!dataset || !dataset.products || !dataset.products.length) return;

    state.renderToken += 1;
    const renderToken = state.renderToken;
    const view = buildSceneViewModel(dataset, state.productId, state.scenarioId);
    state.productId = view.product.id;
    const imageReady = await updateImage(
      section,
      imageData,
      view,
      preloadCache,
      () => renderToken === state.renderToken
    );

    if (!imageReady || renderToken !== state.renderToken) return;
    renderView(section, state, view);
  }

  function preloadScenesNearViewport(section, imageData, preloadCache) {
    const preloadScenes = () => {
      if (!imageData || !imageData.products) return;

      const preloadTasks = [];
      Object.values(imageData.products).forEach((productImages) => {
        ['indoor', 'outdoor'].forEach((scenarioId) => {
          const sources = resolveImageSources(productImages, scenarioId);
          preloadTasks.push(preloadImageSource(getViewportImageSource(sources), preloadCache));
        });
      });
      Promise.allSettled(preloadTasks);
    };

    if (typeof IntersectionObserver === 'undefined') {
      preloadScenes();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      preloadScenes();
    }, { rootMargin: '600px 0px' });

    observer.observe(section);
  }

  function init(section) {
    if (!section || section.dataset.initialized === 'true') return;

    const dataset = parseJson(section, '[data-scenario-data]');
    const imageData = parseJson(section, '[data-scenario-images]');
    if (!dataset || !dataset.products || !dataset.products.length) return;

    const firstButton = section.querySelector('[data-product-id]');
    const state = {
      productId: firstButton ? firstButton.dataset.productId : dataset.products[0].id,
      scenarioId: 'indoor',
      renderToken: 0
    };
    const preloadCache = new Map();

    section.dataset.initialized = 'true';
    syncImageAspectRatio(section);

    section.querySelectorAll('[data-product-id]').forEach((button) => {
      button.addEventListener('click', () => {
        state.productId = button.dataset.productId;
        update(section, state, dataset, imageData, preloadCache);
      });
    });

    section.querySelectorAll('[data-scenario-id]').forEach((button) => {
      button.addEventListener('click', () => {
        state.scenarioId = button.dataset.scenarioId;
        update(section, state, dataset, imageData, preloadCache);
      });
    });

    const initialView = buildSceneViewModel(dataset, state.productId, state.scenarioId);
    state.productId = initialView.product.id;
    renderView(section, state, initialView);
    preloadScenesNearViewport(section, imageData, preloadCache);
  }

  function mountAll(scope) {
    if (!root || !root.document) return;
    const searchRoot = scope && scope.querySelectorAll ? scope : root.document;

    if (searchRoot.matches && searchRoot.matches('[data-scenario-showcase]')) init(searchRoot);
    searchRoot.querySelectorAll('[data-scenario-showcase]').forEach(init);
  }

  return {
    clampPercent,
    formatDeviceValue,
    resolveImageSources,
    getMobileAnchor,
    getMobileCallout,
    buildMobileLeaderPoints,
    buildSceneViewModel,
    syncImageAspectRatio,
    renderDesktopAnnotations,
    renderMobileHotspots,
    renderLegend,
    init,
    mountAll
  };
});
